import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const emptyPackage = {
  name: '',
  description: '',
  price: 0,
  currency: 'usd',
  features: [],
  popular: false,
  order: 0,
  active: true,
};

const PackagesManager = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [featuresText, setFeaturesText] = useState('');

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await adminApi.getPackages();
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFeaturesText(pkg.features?.join('\n') || '');
    } else {
      setEditingPackage({ ...emptyPackage, order: packages.length });
      setFeaturesText('');
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingPackage(null);
    setFeaturesText('');
  };

  const handleChange = (e) => {
    const value = e.target.name === 'price' ? parseFloat(e.target.value) || 0 : e.target.value;
    setEditingPackage({ ...editingPackage, [e.target.name]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const packageData = {
        ...editingPackage,
        features: featuresText.split('\n').filter(f => f.trim()),
      };

      if (editingPackage.id) {
        await adminApi.updatePackage(editingPackage.id, packageData);
        toast.success('Package updated successfully');
      } else {
        await adminApi.createPackage(packageData);
        toast.success('Package created successfully');
      }
      fetchPackages();
      closeDialog();
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error('Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    try {
      await adminApi.deletePackage(id);
      toast.success('Package deleted');
      fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete package';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="packages-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Pricing Packages</h1>
          <p className="text-muted-foreground">Manage your pricing tiers with Stripe integration.</p>
        </div>
        <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-package-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Package
        </Button>
      </div>

      <div className="admin-card mb-6 bg-yellow-500/10 border-yellow-500/20">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-200">
              <strong>Note:</strong> Packages with completed purchases cannot be deleted. Prices are charged via Stripe Checkout in test mode.
            </p>
          </div>
        </div>
      </div>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Price</th>
              <th>Popular</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {packages.map((pkg) => (
              <tr key={pkg.id} data-testid={`package-row-${pkg.id}`}>
                <td className="font-medium">{pkg.name}</td>
                <td className="text-primary font-mono">
                  ${pkg.price?.toLocaleString()} {pkg.currency?.toUpperCase()}
                </td>
                <td>
                  {pkg.popular && (
                    <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                      Popular
                    </span>
                  )}
                </td>
                <td>
                  <span className={pkg.active ? 'badge-success' : 'badge-error'}>
                    {pkg.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDialog(pkg)}
                      data-testid={`edit-package-${pkg.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(pkg.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-package-${pkg.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {packages.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted-foreground py-8">
                  No packages yet. Add your first pricing package.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0f172a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingPackage?.id ? 'Edit Package' : 'Add Package'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Package Name</label>
              <Input
                name="name"
                value={editingPackage?.name || ''}
                onChange={handleChange}
                placeholder="Pro"
                className="input-neon"
                data-testid="package-name-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <Textarea
                name="description"
                value={editingPackage?.description || ''}
                onChange={handleChange}
                placeholder="Perfect for growing businesses"
                rows={2}
                className="input-neon resize-none"
                data-testid="package-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Price (USD)</label>
                <Input
                  name="price"
                  type="number"
                  step="0.01"
                  value={editingPackage?.price || 0}
                  onChange={handleChange}
                  placeholder="999.00"
                  className="input-neon"
                  data-testid="package-price-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Currency</label>
                <Input
                  name="currency"
                  value={editingPackage?.currency || 'usd'}
                  onChange={handleChange}
                  placeholder="usd"
                  className="input-neon"
                  data-testid="package-currency-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Features (one per line)</label>
              <Textarea
                value={featuresText}
                onChange={(e) => setFeaturesText(e.target.value)}
                placeholder="10 Pages Website&#10;Custom Design&#10;3 Months Support"
                rows={4}
                className="input-neon resize-none"
                data-testid="package-features-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="form-label">Mark as Popular</label>
              <Switch
                checked={editingPackage?.popular ?? false}
                onCheckedChange={(checked) => setEditingPackage({ ...editingPackage, popular: checked })}
                data-testid="package-popular-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="form-label">Active</label>
              <Switch
                checked={editingPackage?.active ?? true}
                onCheckedChange={(checked) => setEditingPackage({ ...editingPackage, active: checked })}
                data-testid="package-active-switch"
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="ghost" onClick={closeDialog}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-package-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesManager;
